import { formatDestination, formatUserResponse } from '@silverfort-nodelibs/common/utils/common';
import {
    AUTHENTICATION_LOG_REASONS,
    DENY_REASONS,
    POLICY_ACTION_TYPES,
    USER_TYPES
} from '@silverfort-nodelibs/common/utils/consts';

import { ReactNode } from 'react';
import { LogEntry } from '../../types';

export const generateHeadline = ({
    data,
    defaultDestinations
}: {
    data: LogEntry;
    defaultDestinations: Record<string, string>;
}) => {
    let isHost = false;
    let usernameSection: string | ReactNode = '';
    let hostNameSection: string | ReactNode = '';
    let serverSection: string | ReactNode = '';

    if (data.hostName && data.hostName.toLowerCase() !== 'n/a') {
        isHost = data.userType !== USER_TYPES.MACHINE_ACCOUNT;
        if (isHost) {
            hostNameSection = <span className="font-bold">{data.hostName}</span>;
        }
    }

    if (data.userGuid || data.userIdentifier) {
        const userName = data.upn || data.userIdentifierDisplayName || data.userIdentifier || data.userGuid;
        if (userName.toLowerCase() !== 'n/a') {
            usernameSection = <span className="font-bold">{userName}</span>;
        }
    }

    const [logDestination] = formatDestination(
        data.server,
        data.serverIp,
        data.protocolType,
        data.directoryType,
        defaultDestinations
    );

    if (logDestination) {
        serverSection = <span className="font-bold">{logDestination}</span>;
    }

    let headlineText: string | ReactNode = 'Unknown policy type';
    const action = data.action;
    const response = data.userResponse;
    const ruleId = data.effectiveRuleId;
    const reason = data.reason;
    const errorCode = data.errorCode;
    const policyType = data.policyType;

    const commonSection = (
        <span>
            Accessed {serverSection} {isHost && <>from {hostNameSection}</>}
        </span>
    );

    switch (policyType) {
        case POLICY_ACTION_TYPES.ALLOW.value:
            if (ruleId && response === AUTHENTICATION_LOG_REASONS.NOT_PAIRED_USER.USER_RESPONSE) {
                headlineText = (
                    <span>
                        {commonSection}
                        Allow, as user is unpaired (per unpaired user policy).
                    </span>
                );
            } else if (!ruleId && response === AUTHENTICATION_LOG_REASONS.NOT_PROTECTED_USER.USER_RESPONSE) {
                headlineText = (
                    <span>
                        {commonSection}
                        Allow, as user is not in an enrolled group.
                    </span>
                );
            } else if (!ruleId) {
                headlineText = (
                    <span>
                        {commonSection}
                        Allow (no policy).
                    </span>
                );
            } else {
                headlineText = (
                    <span>
                        {commonSection}
                        Allow, according to policy for user.
                    </span>
                );
            }

            break;

        case POLICY_ACTION_TYPES.DENY.value:
            let denyReason = '';

            if (ruleId && response === AUTHENTICATION_LOG_REASONS.NOT_PAIRED_USER.USER_RESPONSE) {
                denyReason = 'as user is unpaired (per unpaired user policy).';
            } else if (!ruleId && response === AUTHENTICATION_LOG_REASONS.NOT_PROTECTED_USER.USER_RESPONSE) {
                denyReason = 'as user is not in an enrolled group.';
            } else if (reason && DENY_REASONS[reason]) {
                denyReason = `due to ${DENY_REASONS[reason]}` + (errorCode ? ` (${errorCode})` : '');
            } else {
                denyReason = 'according to policy for user.';
            }
            headlineText = (
                <span>
                    Attempted to access {commonSection}
                    Blocked, {denyReason}
                </span>
            );
            break;

        case POLICY_ACTION_TYPES.MFA.value:
            const [userResponse] = formatUserResponse(response, data.effectiveRuleAction);
            const responseNote = userResponse !== 'n/a' ? ` MFA Response: ${userResponse}` : '';

            if (action) {
                headlineText = (
                    <span>
                        {usernameSection ? usernameSection : ''} Accessed {commonSection} and validated by MFA.{' '}
                        {responseNote}
                    </span>
                );
            } else if (response === AUTHENTICATION_LOG_REASONS.TIMEOUT.USER_RESPONSE) {
                headlineText = (
                    <span>
                        {usernameSection ? usernameSection : ''} Attempted to access {serverSection}{' '}
                        {isHost && <>from {hostNameSection} </>}
                        Blocked, as user did not respond to MFA.
                        {responseNote && <> {responseNote}</>}
                    </span>
                );
            } else {
                headlineText = (
                    <span>
                        {usernameSection ? usernameSection : ''} Attempted to access {serverSection}{' '}
                        {isHost && <>from {hostNameSection} </>}
                        and was rejected by MFA.{responseNote && <> {responseNote}</>}
                    </span>
                );
            }
            break;

        case POLICY_ACTION_TYPES.NOTIFY.value:
            headlineText = <span>Accessed {commonSection}and sent notification to syslog.</span>;
            break;
        default:
            break;
    }

    return headlineText;
};
